/**
 * LeadLifecycleSections (Iter 66)
 *
 * Three structured sub-documents on the Lead that bridge Demo/POC to a
 * commercial Proposal:
 *
 *   1. Requirement Capture   — business needs, integrations, pain points,
 *                              compliance, timelines, current vendor.
 *   2. Scope Definition      — included/excluded scope, customizations,
 *                              client responsibilities, support scope.
 *                              Operator-controlled FREEZE locks the doc;
 *                              UNFREEZE is Primary-Admin only.
 *   3. Proposal Readiness    — billing GSTIN, decision makers, commercial
 *                              model, technical feasibility, signoff.
 *
 * The frozen scope + signed readiness become statutorily-required gates
 * before /convert-to-proposal; the proposal also SNAPSHOTS the scope at
 * conversion so future lead edits never alter historical proposal
 * context (matches the existing PO/invoice immutability patterns).
 *
 * All narrative fields use Textareas (multi-line) per the user's
 * directive — enterprise discussions are detailed and iterative.
 */
import { useEffect, useState } from "react";
import {
    ClipboardText, ListChecks, Handshake, LockKey, LockKeyOpen, CheckCircle,
    Pencil, Snowflake, Signature, Warning, Plus, X,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import FormDialog, { FormDialogCancel } from "../../components/FormDialog";
import { DialogFooter } from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const COMMERCIAL_MODELS = ["One-time", "Subscription", "Hybrid", "Milestone-based", "Other"];
const TECH_FEASIBILITY = ["Confirmed", "Pending", "Rejected"];

// Iter 67.2 — Default narrative used to prefill the Included-scope textarea
// when the operator opens the Define Scope dialog on a lead with no scope
// yet recorded. Replaces the previous structured product-picker on Rahul's
// 2026-05-28 direction: scope is a flexible engagement narrative, not a
// rigid row-based picker. Promoting this to Admin → Settings is on the
// backlog if operational usage justifies dynamic editing.
//
// The `{client_name}` placeholder is substituted at prefill time from the
// lead's `company` field (or a sensible fallback if the field is empty).
const DEFAULT_INCLUDED_SCOPE_TEMPLATE = `To deliver and support the implementation of REEDOS platform modules and related services with the objective of enabling an efficient, scalable and technology-driven wealth management and operational ecosystem for {client_name}.`;

const buildDefaultIncludedScope = (lead) => {
    const name = (lead?.company || lead?.suggested_billing?.client_name || "the client").trim();
    return DEFAULT_INCLUDED_SCOPE_TEMPLATE.replace("{client_name}", name);
};

/* ----------------------------- Top-level ------------------------------ */

export default function LeadLifecycleSections({ lead, onChanged }) {
    const [report, setReport] = useState(null);
    const { user } = useAuth();
    const isPrimaryAdmin = !!user?.is_primary_admin;

    const reload = async () => {
        try {
            const { data } = await api.get(`/leads/${lead.id}/readiness-report`);
            setReport(data);
        } catch (err) {
            // Lead detail page also crashes loudly on errors — keep it silent here.
            console.error("readiness-report failed", err);
        }
    };

    useEffect(() => { reload(); }, [lead.id, lead.updated_at]);

    const handleChanged = () => {
        reload();
        onChanged?.();
    };

    return (
        <div className="space-y-6" data-testid="lead-lifecycle-sections">
            {/* Readiness summary strip */}
            {report && (
                <ReadinessStrip report={report} />
            )}

            <RequirementCaptureCard
                lead={lead}
                report={report?.requirement_capture}
                onChanged={handleChanged}
            />

            <ScopeDefinitionCard
                lead={lead}
                report={report?.scope_definition}
                isPrimaryAdmin={isPrimaryAdmin}
                onChanged={handleChanged}
            />

            <ProposalReadinessCard
                lead={lead}
                report={report?.proposal_readiness}
                isPrimaryAdmin={isPrimaryAdmin}
                onChanged={handleChanged}
            />
        </div>
    );
}

/* ---------------------- Readiness summary strip ----------------------- */

function ReadinessStrip({ report }) {
    const ready = report.ready_for_proposal;
    const advisories = report.advisory_issues || [];
    const hasAdvisory = ready && advisories.length > 0;
    return (
        <div
            className={
                "reedos-card p-4 flex items-center gap-3 "
                + (ready && !hasAdvisory
                    ? "border-emerald-200 bg-emerald-50/40"
                    : ready && hasAdvisory
                        ? "border-sky-200 bg-sky-50/40"
                        : "border-amber-200 bg-amber-50/40")
            }
            data-testid="lead-readiness-strip"
        >
            {ready && !hasAdvisory
                ? <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />
                : <Warning size={20} weight="fill" className={ready ? "text-sky-600 shrink-0" : "text-amber-600 shrink-0"} />}
            <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800">
                    {ready
                        ? "Lead is READY for proposal conversion"
                        : "Lead is NOT yet ready to convert to a proposal"}
                </div>
                {!ready && report.blocking_issues.length > 0 && (
                    <div className="text-xs text-slate-600 mt-0.5">
                        Blocking: {report.blocking_issues.join(" · ")}
                    </div>
                )}
                {ready && !hasAdvisory && (
                    <div className="text-xs text-slate-600 mt-0.5">
                        Scope frozen. Convert-to-proposal will snapshot the scope. Proposal Readiness can be completed any time.
                    </div>
                )}
                {ready && hasAdvisory && (
                    // Iter 67.3 — Soft advisory channel (e.g. signoff still
                    // pending). Conversion is allowed; operator informed.
                    <div className="text-xs text-slate-600 mt-0.5">
                        Advisory: {advisories.join(" · ")}
                    </div>
                )}
            </div>
        </div>
    );
}

/* --------------------- Card 1 — Requirement Capture ------------------- */

// Iter 67.10 — Readiness chip strip metadata. Mirrors the same 6-field
// chip pattern from Iter 67.8 (scope) and 67.9 (proposal readiness),
// but with 9 signals matching the operator-visible field list. Order
// here drives visual order. `kind` controls how _has_content is
// evaluated per field type.
const CAPTURE_SIGNALS = [
    { key: "business_requirement",     label: "Business requirement",   kind: "string" },
    { key: "current_vendor_system",    label: "Current system",         kind: "string" },
    { key: "modules_requested",        label: "Modules",                kind: "list"   },
    { key: "reports_required",         label: "Reports",                kind: "list"   },
    { key: "integrations",             label: "Integrations",           kind: "integration-list" },
    { key: "compliance_expectations",  label: "Compliance",             kind: "list"   },
    { key: "deployment_expectations",  label: "Deployment",             kind: "string" },
    { key: "timelines",                label: "Timelines",              kind: "string" },
    { key: "pain_points",              label: "Pain points",            kind: "list"   },
];

function RequirementCaptureCard({ lead, report, onChanged }) {
    const [open, setOpen] = useState(false);
    const data = lead.requirement_capture || {};
    const pct = report?.completion_pct ?? 0;

    return (
        <div className="reedos-card p-5" data-testid="lead-card-requirement-capture">
            <SectionHeader
                icon={<ClipboardText size={18} weight="bold" className="text-sky-600" />}
                title="Requirement Capture"
                subtitle="What the client needs, what they use today, where it hurts"
                pct={pct}
                onEdit={() => setOpen(true)}
            />

            {/* Iter 67.10 — Lightweight readiness chip strip (advisory only,
                non-blocking, non-score-based). Mirrors the pattern from
                Iter 67.8 (Scope Definition) and Iter 67.9 (Proposal
                Readiness). This card has no freeze/sign-off lifecycle,
                so the strip stays visible at all times — there's no
                read-only end state to hide it behind. */}
            <div
                className="mt-3 flex flex-wrap gap-1.5"
                data-testid="capture-chip-strip"
                aria-label="Requirement capture signals"
            >
                {CAPTURE_SIGNALS.map(({ key, label, kind }) => {
                    const v = data[key];
                    let filled;
                    if (kind === "string") {
                        filled = typeof v === "string" && v.trim() !== "";
                    } else if (kind === "list") {
                        filled = Array.isArray(v) && v.length > 0;
                    } else if (kind === "integration-list") {
                        // Integrations entries with a .name set count as content
                        filled = Array.isArray(v) && v.some(it => (it?.name || "").trim() !== "");
                    } else {
                        filled = !!v;
                    }
                    return (
                        <span
                            key={key}
                            data-testid={`capture-chip-${key}`}
                            data-filled={filled ? "1" : "0"}
                            title={filled ? `${label} — captured` : `${label} — pending`}
                            className={
                                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium "
                                + (filled
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-slate-200 bg-slate-50 text-slate-400")
                            }
                        >
                            <span
                                aria-hidden="true"
                                className={
                                    "h-1.5 w-1.5 rounded-full "
                                    + (filled
                                        ? "bg-emerald-500"
                                        : "border border-slate-300 bg-transparent")
                                }
                            />
                            {label}
                        </span>
                    );
                })}
            </div>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-5">
                <Field label="Business requirement" value={data.business_requirement} multiline />
                <Field label="Current vendor / system" value={data.current_vendor_system} multiline />
                <Field label="Modules requested" value={(data.modules_requested || []).join(", ")} />
                <Field label="Reports required" value={(data.reports_required || []).join(", ")} />
                <Field label="Integrations" value={(data.integrations || []).map(i => i.name).join(", ")} />
                <Field label="Compliance expectations" value={(data.compliance_expectations || []).join(", ")} />
                <Field label="Deployment expectations" value={data.deployment_expectations} multiline />
                <Field label="Timelines" value={data.timelines} multiline />
                <Field label="Pain points" value={(data.pain_points || []).join(" · ")} multiline className="sm:col-span-2" />
            </div>

            <RequirementCaptureDialog
                open={open}
                onOpenChange={setOpen}
                lead={lead}
                onSaved={() => { setOpen(false); onChanged?.(); }}
            />
        </div>
    );
}

function RequirementCaptureDialog({ open, onOpenChange, lead, onSaved }) {
    const initial = lead.requirement_capture || {};
    const [form, setForm] = useState(() => seedReq(initial));
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (open) setForm(seedReq(initial)); }, [open]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/leads/${lead.id}/requirement-capture`, {
                business_requirement: form.business_requirement || null,
                modules_requested: toList(form.modules_requested),
                integrations: parseIntegrations(form.integrations_text),
                reports_required: toList(form.reports_required),
                compliance_expectations: toList(form.compliance_expectations),
                deployment_expectations: form.deployment_expectations || null,
                timelines: form.timelines || null,
                current_vendor_system: form.current_vendor_system || null,
                pain_points: toList(form.pain_points, "\n"),
            });
            toast.success("Requirement capture saved.");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save requirement capture"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Capture Requirements"
            description="Document the client's needs, current system, and pain points. All fields are optional — save iteratively as conversations unfold."
            testId="dialog-requirement-capture"
            contentClassName="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        >
            <div className="space-y-4">
                <BigTextarea
                    label="Business requirement"
                    placeholder="What is the client trying to solve? Narrative is welcome — be specific."
                    value={form.business_requirement}
                    onChange={v => setForm({ ...form, business_requirement: v })}
                    rows={4}
                    testId="req-business"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                    <SmallList
                        label="Modules requested"
                        placeholder="One per line — e.g. AMC tracking, GST filing"
                        value={form.modules_requested}
                        onChange={v => setForm({ ...form, modules_requested: v })}
                        testId="req-modules"
                    />
                    <SmallList
                        label="Reports required"
                        placeholder="One per line"
                        value={form.reports_required}
                        onChange={v => setForm({ ...form, reports_required: v })}
                        testId="req-reports"
                    />
                </div>
                <BigTextarea
                    label="Integrations"
                    placeholder="One per line, format: Name | Type | Notes (e.g. Tally Prime | ERP | Daybook + GST sync)"
                    value={form.integrations_text}
                    onChange={v => setForm({ ...form, integrations_text: v })}
                    rows={3}
                    testId="req-integrations"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                    <SmallList
                        label="Compliance expectations"
                        placeholder="One per line — e.g. GSTR-1, TDS Sec 194Q"
                        value={form.compliance_expectations}
                        onChange={v => setForm({ ...form, compliance_expectations: v })}
                        testId="req-compliance"
                    />
                    <BigTextarea
                        label="Deployment expectations"
                        placeholder="Cloud / on-prem / hybrid, hosting region, etc."
                        value={form.deployment_expectations}
                        onChange={v => setForm({ ...form, deployment_expectations: v })}
                        rows={3}
                        testId="req-deployment"
                    />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <BigTextarea
                        label="Timelines"
                        placeholder="Go-live target, urgency drivers, milestones..."
                        value={form.timelines}
                        onChange={v => setForm({ ...form, timelines: v })}
                        rows={3}
                        testId="req-timelines"
                    />
                    <BigTextarea
                        label="Current vendor / system"
                        placeholder="What are they using today? Migration considerations?"
                        value={form.current_vendor_system}
                        onChange={v => setForm({ ...form, current_vendor_system: v })}
                        rows={3}
                        testId="req-current-vendor"
                    />
                </div>
                <BigTextarea
                    label="Pain points"
                    placeholder="One per line — the specific friction points driving this engagement."
                    value={form.pain_points}
                    onChange={v => setForm({ ...form, pain_points: v })}
                    rows={4}
                    testId="req-pain-points"
                />
            </div>
            <DialogFooter className="mt-5">
                <FormDialogCancel />
                <Button onClick={save} disabled={saving} data-testid="req-save">
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

function seedReq(d) {
    return {
        business_requirement: d.business_requirement || "",
        modules_requested: (d.modules_requested || []).join("\n"),
        integrations_text: (d.integrations || []).map(i =>
            `${i.name || ""}${i.type ? " | " + i.type : ""}${i.notes ? " | " + i.notes : ""}`
        ).join("\n"),
        reports_required: (d.reports_required || []).join("\n"),
        compliance_expectations: (d.compliance_expectations || []).join("\n"),
        deployment_expectations: d.deployment_expectations || "",
        timelines: d.timelines || "",
        current_vendor_system: d.current_vendor_system || "",
        pain_points: (d.pain_points || []).join("\n"),
    };
}

function parseIntegrations(text) {
    if (!text || !text.trim()) return null;
    return text.split("\n").map(l => l.trim()).filter(Boolean).map(line => {
        const parts = line.split("|").map(p => p.trim());
        return { name: parts[0], type: parts[1] || "", notes: parts[2] || "" };
    });
}

/* --------------------- Card 2 — Scope Definition ---------------------- */

// Iter 67.8 — Readiness chip strip metadata. Mirrors the 8 SCOPE_SIGNALS
// the backend validator inspects in routes_leads.py:_has_content(). Order
// here defines visual order in the UI (most-commonly-used signals first).
const SCOPE_READINESS_SIGNALS = [
    { key: "included_scope_text",        label: "Included scope" },
    { key: "excluded_scope",             label: "Exclusions" },
    { key: "customizations",             label: "Customizations" },
    { key: "dependencies",               label: "Dependencies" },
    { key: "client_responsibilities",    label: "Client responsibilities" },
    { key: "implementation_assumptions", label: "Assumptions" },
    { key: "support_scope",              label: "Support scope" },
    { key: "included_scope",             label: "Legacy items" },
];

function ScopeDefinitionCard({ lead, report, isPrimaryAdmin, onChanged }) {
    const [open, setOpen] = useState(false);
    const [unfreezeOpen, setUnfreezeOpen] = useState(false);
    const data = lead.scope_definition || {};
    const pct = report?.completion_pct ?? 0;
    const frozen = !!data.frozen_at;

    // Iter 67.6 — client-side mirror of the broadened backend validator.
    // Freeze button stays disabled until at least one of the 8 scope
    // signals carries meaningful operator input. Strings are .trim()'d
    // so whitespace-only entries don't unlock the action; lists must
    // have at least one element. This avoids the dead-click UX where an
    // operator hits Freeze on a fresh lead, sees a 400 toast, and has
    // to guess which field to populate.
    const hasScopeContent = (
        (data.included_scope_text || "").trim() !== ""
        || (Array.isArray(data.included_scope) && data.included_scope.length > 0)
        || (Array.isArray(data.excluded_scope) && data.excluded_scope.length > 0)
        || (Array.isArray(data.customizations) && data.customizations.length > 0)
        || (Array.isArray(data.dependencies) && data.dependencies.length > 0)
        || (Array.isArray(data.client_responsibilities) && data.client_responsibilities.length > 0)
        || (Array.isArray(data.implementation_assumptions) && data.implementation_assumptions.length > 0)
        || (data.support_scope || "").trim() !== ""
    );

    const freeze = async () => {
        if (!window.confirm(
            "Freeze the scope? After freezing, the scope is immutable — edits require a Primary Admin unfreeze. "
            + "Freezing is required before this lead can be converted to a proposal."
        )) return;
        try {
            await api.post(`/leads/${lead.id}/scope-definition/freeze`);
            toast.success("Scope frozen.", { description: "Scope is now immutable until a Primary Admin unfreezes." });
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to freeze scope"));
        }
    };

    return (
        <div className="reedos-card p-5" data-testid="lead-card-scope-definition">
            <SectionHeader
                icon={<ListChecks size={18} weight="bold" className="text-cyan-600" />}
                title="Scope Definition"
                subtitle="Inclusions, exclusions, customizations, responsibilities — locked at freeze"
                pct={pct}
                badge={frozen ? {
                    label: "Frozen",
                    tone: "bg-cyan-50 text-cyan-800 border-cyan-300",
                    icon: <Snowflake size={11} weight="fill" />,
                } : null}
                onEdit={frozen ? null : () => setOpen(true)}
                actions={frozen ? (
                    <div className="flex items-center gap-2">
                        {isPrimaryAdmin && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setUnfreezeOpen(true)}
                                className="gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50"
                                data-testid="scope-unfreeze-btn"
                            >
                                <LockKeyOpen size={12} weight="bold" /> Unfreeze
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col items-end gap-1">
                        <Button
                            size="sm"
                            onClick={freeze}
                            disabled={!hasScopeContent}
                            className={hasScopeContent
                                ? "gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white"
                                : "gap-1.5 bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200 hover:bg-slate-100"}
                            title={hasScopeContent ? "Freeze this scope" : "Add scope details before freezing"}
                            data-testid="scope-freeze-btn"
                        >
                            <LockKey size={12} weight="bold" /> Freeze Scope
                        </Button>
                        {!hasScopeContent && (
                            <span
                                className="text-[10px] text-slate-500 italic"
                                data-testid="scope-freeze-hint"
                            >
                                Add scope details before freezing.
                            </span>
                        )}
                    </div>
                )}
            />

            {/* Iter 67.8 — Lightweight readiness chip strip (advisory only,
                non-blocking, non-score-based). Surfaces which of the 8
                scope sub-signals carry content so operators can scan
                "what's left to fill?" without expanding every section.
                Hidden once the scope is frozen — the strip is a pre-freeze
                aid, not a permanent decoration. */}
            {!frozen && (
                <div
                    className="mt-3 flex flex-wrap gap-1.5"
                    data-testid="scope-readiness-strip"
                    aria-label="Scope readiness signals"
                >
                    {SCOPE_READINESS_SIGNALS.map(({ key, label }) => {
                        const v = data[key];
                        const filled = typeof v === "string"
                            ? v.trim() !== ""
                            : Array.isArray(v) && v.length > 0;
                        return (
                            <span
                                key={key}
                                data-testid={`scope-readiness-chip-${key}`}
                                data-filled={filled ? "1" : "0"}
                                title={filled ? `${label} — populated` : `${label} — empty`}
                                className={
                                    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium "
                                    + (filled
                                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                        : "border-slate-200 bg-slate-50 text-slate-400")
                                }
                            >
                                <span
                                    aria-hidden="true"
                                    className={
                                        "h-1.5 w-1.5 rounded-full "
                                        + (filled
                                            ? "bg-emerald-500"
                                            : "border border-slate-300 bg-transparent")
                                    }
                                />
                                {label}
                            </span>
                        );
                    })}
                </div>
            )}

            {frozen && (
                <div className="mt-3 text-xs text-cyan-900/80 bg-cyan-50 border border-cyan-200 rounded px-3 py-2"
                     data-testid="scope-frozen-banner">
                    🔒 Frozen on {formatDateTime(data.frozen_at)} by {data.frozen_by?.name || data.frozen_by?.email}.
                    {(data.unfrozen_history || []).length > 0 && (
                        <> · Previously unfrozen {data.unfrozen_history.length}×.</>
                    )}
                </div>
            )}

            <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {data.included_scope_text ? (
                    // Iter 67.2 — Free-text scope narrative (replaces the
                    // structured included_scope[] readback). Spans both
                    // columns; preserves operator's line breaks.
                    <div className="sm:col-span-2">
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Included scope</Label>
                        <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed"
                             data-testid="scope-included-text-display">
                            {data.included_scope_text}
                        </div>
                    </div>
                ) : (
                    <ScopeList label="Included scope" items={data.included_scope} structured />
                )}
                <ScopeList label="Excluded scope" items={(data.excluded_scope || []).map(s => ({ title: s }))} />
                <ScopeList label="Customizations" items={data.customizations} structured />
                <ScopeList label="Dependencies" items={(data.dependencies || []).map(s => ({ title: s }))} />
                <ScopeList label="Client responsibilities" items={(data.client_responsibilities || []).map(s => ({ title: s }))} />
                <ScopeList label="Implementation assumptions" items={(data.implementation_assumptions || []).map(s => ({ title: s }))} />
                <Field label="Support scope" value={data.support_scope} multiline className="sm:col-span-2" />
            </div>

            <ScopeDefinitionDialog
                open={open}
                onOpenChange={setOpen}
                lead={lead}
                onSaved={() => { setOpen(false); onChanged?.(); }}
            />
            <ScopeUnfreezeDialog
                open={unfreezeOpen}
                onOpenChange={setUnfreezeOpen}
                lead={lead}
                onSaved={() => { setUnfreezeOpen(false); onChanged?.(); }}
            />
        </div>
    );
}

function ScopeList({ label, items, structured }) {
    if (!items || items.length === 0) {
        return (
            <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
                <div className="text-sm text-slate-400 mt-0.5">—</div>
            </div>
        );
    }
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
            <ul className="mt-1 space-y-1.5">
                {items.map((it, idx) => (
                    <li key={idx} className="text-sm text-slate-800 leading-snug">
                        <div className="font-medium">{it.title}</div>
                        {structured && it.suggested_amount != null && (
                            <span className="text-xs text-slate-500"> · ₹{Number(it.suggested_amount).toLocaleString("en-IN")}</span>
                        )}
                        {it.description && (
                            <div className="text-xs text-slate-500 leading-snug">{it.description}</div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function ScopeDefinitionDialog({ open, onOpenChange, lead, onSaved }) {
    const initial = lead.scope_definition || {};
    // Iter 67.2 — Included scope is now a single free-text narrative
    // (`included_scope_text`). Legacy `included_scope[]` array kept on the
    // wire for backward compat with historical leads, but never written
    // from the UI any more.
    const [includedScopeText, setIncludedScopeText] = useState("");
    const [customizationItems, setCustomizationItems] = useState(() => initial.customizations || []);
    const [form, setForm] = useState(() => seedScope(initial));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            const legacyItems = initial.included_scope || [];
            // Prefill rules:
            //   1. If saved text exists → use it verbatim.
            //   2. Else if legacy structured items exist → convert each item's
            //      title to a bullet so the operator sees their old content
            //      and can edit it as text from here on.
            //   3. Else → drop in the default REEDOS narrative template with
            //      the client name substituted.
            const saved = (initial.included_scope_text || "").trim();
            if (saved) {
                setIncludedScopeText(initial.included_scope_text || "");
            } else if (legacyItems.length > 0) {
                const lines = legacyItems
                    .filter((i) => i.title?.trim())
                    .map((i) => `• ${i.title.trim()}${i.description ? ` — ${i.description}` : ""}`)
                    .join("\n");
                setIncludedScopeText(lines || buildDefaultIncludedScope(lead));
            } else {
                const def = buildDefaultIncludedScope(lead);
                setIncludedScopeText(def);
                // Iter 67.5 — Auto-persist the default narrative when the
                // lead has NO scope engagement yet (no text, no structured
                // items, no exclusions / customizations / dependencies /
                // responsibilities / assumptions / support scope). Mirrors
                // the visual prefill into the DB so Freeze Scope can
                // succeed without an intermediate explicit Save click —
                // the prior UX created a misleading impression of
                // "already saved". Auto-PATCH is skipped the moment ANY
                // scope content exists so operator intent is never
                // clobbered.
                const completelyEmpty =
                    legacyItems.length === 0
                    && (initial.excluded_scope || []).length === 0
                    && (initial.customizations || []).length === 0
                    && (initial.dependencies || []).length === 0
                    && (initial.client_responsibilities || []).length === 0
                    && (initial.implementation_assumptions || []).length === 0
                    && !(initial.support_scope || "").trim();
                if (completelyEmpty) {
                    api.patch(`/leads/${lead.id}/scope-definition`, {
                        included_scope_text: def,
                        included_scope: [],
                    }).then(() => {
                        // Refresh parent so subsequent reads see the persisted
                        // narrative. Dialog stays open with the same value
                        // already showing — operator perceives no flicker.
                        onSaved?.();
                    }).catch((err) => {
                        // Soft fail — operator still sees the prefill and
                        // can hit Save explicitly. Surface as a discreet
                        // toast so the gap isn't silent.
                        // eslint-disable-next-line no-console
                        console.warn("[scope] auto-persist failed:", err);
                        toast.info("Default scope shown — click Save to persist before Freezing.");
                    });
                }
            }
            setCustomizationItems(initial.customizations || []);
            setForm(seedScope(initial));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/leads/${lead.id}/scope-definition`, {
                included_scope_text: includedScopeText.trim() || null,
                // Clear the legacy structured array — text is now the source
                // of truth. Backward compat reads still find legacy items
                // for historical leads that haven't been re-edited.
                included_scope: [],
                excluded_scope: toList(form.excluded_scope),
                customizations: customizationItems.filter(i => i.title?.trim()),
                dependencies: toList(form.dependencies),
                client_responsibilities: toList(form.client_responsibilities),
                implementation_assumptions: toList(form.implementation_assumptions),
                support_scope: form.support_scope || null,
            });
            toast.success("Scope definition saved.");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save scope definition"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Define Scope"
            description="Engagement narrative + structured exclusions / dependencies / responsibilities. Locks the commercial intent before proposal."
            testId="dialog-scope-definition"
            contentClassName="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        >
            <div className="space-y-5">
                <BigTextarea
                    label="Included scope"
                    placeholder="Describe what's being delivered under this engagement."
                    value={includedScopeText}
                    onChange={setIncludedScopeText}
                    rows={9}
                    testId="scope-included-text"
                    hint="Free-form narrative — prefilled with a default that fits most engagements; edit freely. Becomes the scope-of-work paragraph carried forward to the proposal."
                />
                <ScopeItemEditor
                    label="Customizations requested"
                    hint="Bespoke work outside the base scope. Tag with a suggested amount for visibility."
                    items={customizationItems}
                    setItems={setCustomizationItems}
                    structured
                    testId="scope-customizations"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                    <BigTextarea
                        label="Excluded scope"
                        placeholder="One per line — what is explicitly OUT of scope."
                        value={form.excluded_scope}
                        onChange={v => setForm({ ...form, excluded_scope: v })}
                        rows={4}
                        testId="scope-excluded"
                    />
                    <BigTextarea
                        label="Dependencies"
                        placeholder="One per line — what must be in place first."
                        value={form.dependencies}
                        onChange={v => setForm({ ...form, dependencies: v })}
                        rows={4}
                        testId="scope-dependencies"
                    />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <BigTextarea
                        label="Client responsibilities"
                        placeholder="One per line"
                        value={form.client_responsibilities}
                        onChange={v => setForm({ ...form, client_responsibilities: v })}
                        rows={4}
                        testId="scope-client-resp"
                    />
                    <BigTextarea
                        label="Implementation assumptions"
                        placeholder="One per line"
                        value={form.implementation_assumptions}
                        onChange={v => setForm({ ...form, implementation_assumptions: v })}
                        rows={4}
                        testId="scope-assumptions"
                    />
                </div>
                <BigTextarea
                    label="Support scope"
                    placeholder="SLA, response times, what's free vs T&M..."
                    value={form.support_scope}
                    onChange={v => setForm({ ...form, support_scope: v })}
                    rows={3}
                    testId="scope-support"
                />
            </div>
            <DialogFooter className="mt-5">
                <FormDialogCancel />
                <Button onClick={save} disabled={saving} data-testid="scope-save">
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

function seedScope(d) {
    return {
        excluded_scope: (d.excluded_scope || []).join("\n"),
        dependencies: (d.dependencies || []).join("\n"),
        client_responsibilities: (d.client_responsibilities || []).join("\n"),
        implementation_assumptions: (d.implementation_assumptions || []).join("\n"),
        support_scope: d.support_scope || "",
    };
}

function ScopeItemEditor({ label, hint, items, setItems, structured, testId, catalogueItems }) {
    const catalogueEnabled = Array.isArray(catalogueItems) && catalogueItems.length > 0;
    // Iter 66.9 — `add()` defaults a new row to "catalogue mode" when the
    // picker is available, else free-text. Free-text fallback is always
    // reachable via the "Use custom item" link inside the row.
    const add = () => setItems([
        ...items,
        {
            title: "",
            description: "",
            suggested_amount: null,
            linked_catalogue_item_id: null,
            // UI-only flag; stripped on save by the truthy-title filter.
            _custom: !catalogueEnabled,
        },
    ]);
    const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
    const update = (idx, patch) => setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));

    // Picking a catalogue item prefills title / suggested_amount / description
    // verbatim. The operator can override afterwards — overrides do NOT
    // unlink the catalogue id (that's the audit trail Iter 67 uses to
    // snapshot AMC%/GST/HSN onto the proposal line at auto-draft time).
    const pickCatalogue = (idx, cid) => {
        if (!cid) {
            // Cleared selection — keep row but drop link + reset prefills
            update(idx, { linked_catalogue_item_id: null });
            return;
        }
        const c = catalogueItems.find((x) => x.id === cid);
        if (!c) return;
        update(idx, {
            linked_catalogue_item_id: c.id,
            title: c.name,
            // Software fee is the headline commercial number for an
            // included scope row. AMC is captured on the proposal line later.
            suggested_amount: c.default_software_fee ?? null,
            description: c.subtitle || c.description || "",
            _custom: false,
        });
    };

    const switchToCustom = (idx) => update(idx, {
        linked_catalogue_item_id: null,
        _custom: true,
    });
    const switchToCatalogue = (idx) => update(idx, { _custom: false });

    return (
        <div data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">{label}</Label>
                    <div className="text-[11px] text-slate-500">{hint}</div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={add} className="gap-1" data-testid={`${testId}-add`}>
                    <Plus size={12} weight="bold" /> Add item
                </Button>
            </div>
            {items.length === 0 && (
                <div className="text-xs text-slate-400 italic px-2 py-1.5 border border-dashed rounded">
                    No items yet — click "Add item" to start.
                </div>
            )}
            {items.length > 0 && (
                <div className="space-y-2">
                    {items.map((it, idx) => {
                        // A row is in "catalogue mode" iff the picker is
                        // available AND the row hasn't been switched to custom
                        // AND (it has a linked id OR it's a brand-new row).
                        const inCatalogueMode = catalogueEnabled
                            && !it._custom
                            && (!!it.linked_catalogue_item_id || (!it.title && it._custom !== true));
                        return (
                            <div key={idx} className="border rounded p-2 bg-slate-50/50 space-y-1.5" data-testid={`${testId}-row-${idx}`}>
                                <div className="flex gap-2">
                                    {inCatalogueMode ? (
                                        <select
                                            value={it.linked_catalogue_item_id || ""}
                                            onChange={(e) => pickCatalogue(idx, e.target.value)}
                                            className="flex-1 bg-white border border-slate-200 rounded px-2 h-9 text-sm"
                                            data-testid={`${testId}-row-${idx}-catalogue-picker`}
                                        >
                                            <option value="">— Pick from Products &amp; Services —</option>
                                            {catalogueItems.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                    {c.default_software_fee != null ? ` · ₹${Number(c.default_software_fee).toLocaleString("en-IN")}` : ""}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <Input
                                            placeholder="Item title"
                                            value={it.title || ""}
                                            onChange={e => update(idx, { title: e.target.value })}
                                            className="bg-white"
                                            data-testid={`${testId}-row-${idx}-title`}
                                        />
                                    )}
                                    {structured && (
                                        <Input
                                            placeholder="Suggested ₹"
                                            type="number"
                                            value={it.suggested_amount ?? ""}
                                            onChange={e => update(idx, { suggested_amount: e.target.value === "" ? null : Number(e.target.value) })}
                                            className="bg-white w-32"
                                            data-testid={`${testId}-row-${idx}-amount`}
                                        />
                                    )}
                                    <Button type="button" variant="ghost" size="sm" onClick={() => remove(idx)} className="text-rose-600" data-testid={`${testId}-row-${idx}-remove`}>
                                        <X size={12} weight="bold" />
                                    </Button>
                                </div>
                                {/* Catalogue ↔ custom switcher — only when the picker is available */}
                                {catalogueEnabled && (
                                    <div className="flex items-center justify-between text-[10px]">
                                        {inCatalogueMode ? (
                                            <button
                                                type="button"
                                                onClick={() => switchToCustom(idx)}
                                                className="text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline"
                                                data-testid={`${testId}-row-${idx}-switch-custom`}
                                            >
                                                Can't find it? Use a custom item →
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => switchToCatalogue(idx)}
                                                className="text-indigo-600 hover:text-indigo-800 underline-offset-2 hover:underline"
                                                data-testid={`${testId}-row-${idx}-switch-catalogue`}
                                            >
                                                ← Pick from Products &amp; Services instead
                                            </button>
                                        )}
                                        {it.linked_catalogue_item_id && (
                                            <span className="font-mono text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`${testId}-row-${idx}-linked-badge`}>
                                                ↪ {it.linked_catalogue_item_id}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <Textarea
                                    placeholder="Description (optional)"
                                    value={it.description || ""}
                                    onChange={e => update(idx, { description: e.target.value })}
                                    rows={2}
                                    className="bg-white text-xs"
                                    data-testid={`${testId}-row-${idx}-description`}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function ScopeUnfreezeDialog({ open, onOpenChange, lead, onSaved }) {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (reason.trim().length < 10) {
            toast.error("Reason must be at least 10 characters.");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/leads/${lead.id}/scope-definition/unfreeze`, { reason: reason.trim() });
            toast.success("Scope unfrozen. Edits are now allowed.");
            setReason("");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to unfreeze scope"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Unfreeze Scope"
            description="Reopens the scope for editing. The reason and your identity are recorded in unfrozen_history[] for audit. Primary Admin only."
            testId="dialog-scope-unfreeze"
        >
            <div className="space-y-3">
                <BigTextarea
                    label="Reason (mandatory, ≥10 chars)"
                    placeholder="Explain why this scope needs to be reopened — auditable record."
                    value={reason}
                    onChange={setReason}
                    rows={4}
                    testId="unfreeze-reason"
                />
            </div>
            <DialogFooter className="mt-4">
                <FormDialogCancel />
                <Button
                    variant="outline"
                    onClick={submit}
                    disabled={saving || reason.trim().length < 10}
                    className="border-amber-300 text-amber-800 hover:bg-amber-50"
                    data-testid="unfreeze-submit"
                >
                    {saving ? "Unfreezing…" : "Confirm Unfreeze"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

/* --------------------- Card 3 — Proposal Readiness -------------------- */

// Iter 67.9 — Readiness chip strip metadata (Proposal Readiness mirror of
// the Scope Definition pattern shipped in Iter 67.8). Same visual
// contract: subtle green/grey populated-state indicators, advisory only,
// non-blocking, non-score-based.
const READINESS_SIGNALS = [
    { key: "billing_gstin",              label: "Billing GSTIN" },
    { key: "billing_address",            label: "Billing address" },
    { key: "commercial_model",           label: "Commercial model" },
    { key: "technical_feasibility",      label: "Technical feasibility" },
    { key: "technical_feasibility_note", label: "Feasibility note" },
    { key: "decision_makers",            label: "Decision makers" },
];

function ProposalReadinessCard({ lead, report, isPrimaryAdmin, onChanged }) {
    const [open, setOpen] = useState(false);
    const [unsignOpen, setUnsignOpen] = useState(false);
    const data = lead.proposal_readiness || {};
    const pct = report?.completion_pct ?? 0;
    const signed = (data.requirement_signoff || "").toLowerCase() === "signed";

    const sign = async () => {
        const note = window.prompt(
            "Sign off on requirements?\n\nOptional note (Cancel to abort):",
            ""
        );
        if (note === null) return;
        try {
            await api.post(`/leads/${lead.id}/proposal-readiness/sign`, { note: note || null });
            toast.success("Requirements signed off.", { description: "Readiness is now locked. Primary Admin can unsign if needed." });
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to sign off"));
        }
    };

    return (
        <div className="reedos-card p-5" data-testid="lead-card-proposal-readiness">
            <SectionHeader
                icon={<Handshake size={18} weight="bold" className="text-amber-600" />}
                title="Proposal Readiness"
                subtitle="Optional — billing, commercial model, decision makers, technical feasibility, signoff. Can be completed any time before proposal close."
                pct={pct}
                badge={signed ? {
                    label: "Signed Off",
                    tone: "bg-emerald-50 text-emerald-800 border-emerald-300",
                    icon: <Signature size={11} weight="fill" />,
                } : null}
                onEdit={signed ? null : () => setOpen(true)}
                actions={signed ? (
                    <div className="flex items-center gap-2">
                        {isPrimaryAdmin && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setUnsignOpen(true)}
                                className="gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50"
                                data-testid="readiness-unsign-btn"
                            >
                                <LockKeyOpen size={12} weight="bold" /> Unsign
                            </Button>
                        )}
                    </div>
                ) : (
                    <Button
                        size="sm"
                        onClick={sign}
                        className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="readiness-sign-btn"
                    >
                        <Signature size={12} weight="bold" /> Sign Off
                    </Button>
                )}
            />

            {/* Iter 67.9 — Lightweight readiness chip strip (advisory only,
                non-blocking, non-score-based). Surfaces which of the 6
                proposal-readiness sub-signals carry content so operators
                can scan "what's left to capture?" at a glance. Hidden
                once the readiness is signed off — the strip is a
                pre-signoff aid, not a permanent decoration. */}
            {!signed && (
                <div
                    className="mt-3 flex flex-wrap gap-1.5"
                    data-testid="readiness-chip-strip"
                    aria-label="Proposal readiness signals"
                >
                    {READINESS_SIGNALS.map(({ key, label }) => {
                        const v = data[key];
                        const filled = typeof v === "string"
                            ? v.trim() !== ""
                            : Array.isArray(v) && v.length > 0;
                        return (
                            <span
                                key={key}
                                data-testid={`readiness-chip-${key}`}
                                data-filled={filled ? "1" : "0"}
                                title={filled ? `${label} — captured` : `${label} — pending`}
                                className={
                                    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium "
                                    + (filled
                                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                        : "border-slate-200 bg-slate-50 text-slate-400")
                                }
                            >
                                <span
                                    aria-hidden="true"
                                    className={
                                        "h-1.5 w-1.5 rounded-full "
                                        + (filled
                                            ? "bg-emerald-500"
                                            : "border border-slate-300 bg-transparent")
                                    }
                                />
                                {label}
                            </span>
                        );
                    })}
                </div>
            )}

            {signed && (
                <div className="mt-3 text-xs text-emerald-900/80 bg-emerald-50 border border-emerald-200 rounded px-3 py-2"
                     data-testid="readiness-signed-banner">
                    🔒 Signed on {formatDateTime(data.signoff_at)} by {data.signoff_by?.name || data.signoff_by?.email}. Card is read-only until a Primary Admin unsigns.
                    {data.signoff_note && <span className="ml-1 italic">Note: {data.signoff_note}</span>}
                    {(data.unsigned_history || []).length > 0 && (
                        <> · Previously unsigned {data.unsigned_history.length}×.</>
                    )}
                </div>
            )}

            <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                <Field label="Billing GSTIN" value={data.billing_gstin} />
                <Field label="Billing address" value={data.billing_address} multiline />
                <Field label="Commercial model" value={data.commercial_model} />
                <Field label="Technical feasibility" value={data.technical_feasibility} />
                <Field label="Feasibility note" value={data.technical_feasibility_note} multiline className="sm:col-span-2" />
                <div className="sm:col-span-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Decision makers</div>
                    {(data.decision_makers || []).length === 0
                        ? <div className="text-sm text-slate-400 mt-0.5">—</div>
                        : (
                            <ul className="mt-1 space-y-1">
                                {data.decision_makers.map((dm, idx) => (
                                    <li key={idx} className="text-sm text-slate-800">
                                        <span className="font-medium">{dm.name}</span>
                                        {dm.role && <span className="text-slate-500"> · {dm.role}</span>}
                                        {dm.email && <span className="text-slate-500"> · {dm.email}</span>}
                                    </li>
                                ))}
                            </ul>
                        )}
                </div>
            </div>

            <ProposalReadinessDialog
                open={open}
                onOpenChange={setOpen}
                lead={lead}
                onSaved={() => { setOpen(false); onChanged?.(); }}
            />
            <ReadinessUnsignDialog
                open={unsignOpen}
                onOpenChange={setUnsignOpen}
                lead={lead}
                onSaved={() => { setUnsignOpen(false); onChanged?.(); }}
            />
        </div>
    );
}

function ReadinessUnsignDialog({ open, onOpenChange, lead, onSaved }) {
    // Iter 66.13 — Mirror of ScopeUnfreezeDialog. Primary Admin only.
    // Reason is mandatory and appended to proposal_readiness.unsigned_history
    // for audit. Server enforces this via the `is_primary_super_admin()`
    // helper in `auth.py` (the wire payload field on `/auth/me` is
    // `is_primary_admin`); the FE check is a soft guard already enforced
    // by the parent card's button visibility.
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (reason.trim().length < 10) {
            toast.error("Reason must be at least 10 characters.");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/leads/${lead.id}/proposal-readiness/unsign`, { reason: reason.trim() });
            toast.success("Readiness unsigned. Card is now editable again.");
            setReason("");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to unsign readiness"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Unsign Proposal Readiness"
            description="Reopens the readiness for editing. The reason and your identity are recorded in unsigned_history[] for audit. Primary Admin only."
            testId="dialog-readiness-unsign"
        >
            <div className="space-y-3">
                <BigTextarea
                    label="Reason (mandatory, ≥10 chars)"
                    placeholder="Explain why this readiness needs to be reopened — auditable record."
                    value={reason}
                    onChange={setReason}
                    rows={4}
                    testId="unsign-reason"
                />
            </div>
            <DialogFooter className="mt-4">
                <FormDialogCancel />
                <Button
                    variant="outline"
                    onClick={submit}
                    disabled={saving || reason.trim().length < 10}
                    className="border-amber-300 text-amber-800 hover:bg-amber-50"
                    data-testid="unsign-submit"
                >
                    {saving ? "Unsigning…" : "Confirm Unsign"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

function ProposalReadinessDialog({ open, onOpenChange, lead, onSaved }) {
    const initial = lead.proposal_readiness || {};
    // Iter 66.13 — Seed cascade for billing GSTIN + address:
    //   1. Existing readiness values (preserve operator input)
    //   2. `lead.suggested_billing` from the server when source === "client"
    //      (an existing Client with matching GSTIN — most authoritative)
    //   3. Lead's own gstin + composed address (Iter 66.6 fallback)
    // The cascade is computed server-side in routes_leads._enrich_lead so
    // the FE doesn't have to choose between sources or run an extra fetch.
    const seedWithLead = () => {
        const seeded = seedReadiness(initial);
        const sugg = lead.suggested_billing || {};
        if (!seeded.billing_gstin) {
            seeded.billing_gstin = sugg.gstin || lead.gstin || "";
        }
        if (!seeded.billing_address) {
            if (sugg.address) {
                seeded.billing_address = sugg.address;
            } else {
                const parts = [lead.address_line1, lead.city, lead.pin_code].filter(Boolean);
                if (parts.length) seeded.billing_address = parts.join(", ");
            }
        }
        return seeded;
    };
    const billingSource = lead.suggested_billing?.source;
    const billingClientName = lead.suggested_billing?.client_name;
    const [form, setForm] = useState(() => seedWithLead());
    const [decisionMakers, setDecisionMakers] = useState(() => initial.decision_makers || []);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(seedWithLead());
            setDecisionMakers(initial.decision_makers || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/leads/${lead.id}/proposal-readiness`, {
                billing_gstin: form.billing_gstin || null,
                billing_address: form.billing_address || null,
                commercial_model: form.commercial_model || null,
                technical_feasibility: form.technical_feasibility || null,
                technical_feasibility_note: form.technical_feasibility_note || null,
                signoff_document_url: form.signoff_document_url || null,
                decision_makers: decisionMakers.filter(d => d.name?.trim()),
            });
            toast.success("Proposal readiness saved.");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save readiness"));
        } finally {
            setSaving(false);
        }
    };

    const addDM = () => setDecisionMakers([...decisionMakers, { name: "", role: "", email: "", phone: "" }]);
    const removeDM = (idx) => setDecisionMakers(decisionMakers.filter((_, i) => i !== idx));
    const updDM = (idx, key, val) => setDecisionMakers(
        decisionMakers.map((d, i) => i === idx ? { ...d, [key]: val } : d)
    );

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Proposal Readiness"
            description="Confirm commercial context before raising a proposal. Signoff is a separate action (Sign Off button)."
            testId="dialog-proposal-readiness"
            contentClassName="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        >
            <div className="space-y-4">
                {/* Iter 66.13 — Source-of-truth banner for billing prefill.
                    Tells the operator WHERE the GSTIN/address came from so
                    they understand any override they're about to make. */}
                {billingSource && (
                    <div
                        className={`text-[11px] px-3 py-1.5 rounded border ${billingSource === "client"
                            ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                            : "bg-amber-50 border-amber-200 text-amber-800"
                            }`}
                        data-testid="readiness-billing-source-banner"
                    >
                        {billingSource === "client" ? (
                            <>↪ Billing GSTIN &amp; address pulled from existing client
                                {billingClientName ? ` "${billingClientName}"` : ""} (matched by GSTIN). Override below if needed.</>
                        ) : (
                            <>↪ Billing pre-filled from this lead&apos;s own GSTIN + address fields. No matching client found yet.</>
                        )}
                    </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Billing GSTIN</Label>
                        <Input
                            value={form.billing_gstin}
                            onChange={e => setForm({ ...form, billing_gstin: e.target.value })}
                            placeholder="e.g. 27ABCDE1234A1Z5"
                            data-testid="readiness-gstin"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Commercial model</Label>
                        <select
                            value={form.commercial_model}
                            onChange={e => setForm({ ...form, commercial_model: e.target.value })}
                            className="block w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                            data-testid="readiness-commercial-model"
                        >
                            <option value="">Pick…</option>
                            {COMMERCIAL_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                </div>
                <BigTextarea
                    label="Billing address"
                    placeholder="Full address for invoicing."
                    value={form.billing_address}
                    onChange={v => setForm({ ...form, billing_address: v })}
                    rows={3}
                    testId="readiness-address"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Technical feasibility</Label>
                        <select
                            value={form.technical_feasibility}
                            onChange={e => setForm({ ...form, technical_feasibility: e.target.value })}
                            className="block w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200"
                            data-testid="readiness-tech-feasibility"
                        >
                            <option value="">Pick…</option>
                            {TECH_FEASIBILITY.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Signoff document URL</Label>
                        <Input
                            value={form.signoff_document_url}
                            onChange={e => setForm({ ...form, signoff_document_url: e.target.value })}
                            placeholder="Optional — link to signed PDF / email thread"
                            data-testid="readiness-signoff-url"
                        />
                    </div>
                </div>
                <BigTextarea
                    label="Technical feasibility note"
                    placeholder="Caveats, partial confirmations, integration risks..."
                    value={form.technical_feasibility_note}
                    onChange={v => setForm({ ...form, technical_feasibility_note: v })}
                    rows={3}
                    testId="readiness-tech-note"
                />
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Decision makers</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addDM} className="gap-1" data-testid="readiness-dm-add">
                            <Plus size={12} weight="bold" /> Add
                        </Button>
                    </div>
                    {decisionMakers.length === 0 && (
                        <div className="text-xs text-slate-400 italic px-2 py-1.5 border border-dashed rounded">
                            No decision makers captured yet.
                        </div>
                    )}
                    <div className="space-y-2">
                        {decisionMakers.map((dm, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`readiness-dm-row-${idx}`}>
                                <Input className="col-span-3 bg-white" placeholder="Name" value={dm.name || ""} onChange={e => updDM(idx, "name", e.target.value)} />
                                <Input className="col-span-3 bg-white" placeholder="Role" value={dm.role || ""} onChange={e => updDM(idx, "role", e.target.value)} />
                                <Input className="col-span-3 bg-white" placeholder="Email" value={dm.email || ""} onChange={e => updDM(idx, "email", e.target.value)} />
                                <Input className="col-span-2 bg-white" placeholder="Phone" value={dm.phone || ""} onChange={e => updDM(idx, "phone", e.target.value)} />
                                <Button type="button" variant="ghost" size="sm" onClick={() => removeDM(idx)} className="text-rose-600 col-span-1">
                                    <X size={12} weight="bold" />
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <DialogFooter className="mt-5">
                <FormDialogCancel />
                <Button onClick={save} disabled={saving} data-testid="readiness-save">
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

function seedReadiness(d) {
    return {
        billing_gstin: d.billing_gstin || "",
        billing_address: d.billing_address || "",
        commercial_model: d.commercial_model || "",
        technical_feasibility: d.technical_feasibility || "",
        technical_feasibility_note: d.technical_feasibility_note || "",
        signoff_document_url: d.signoff_document_url || "",
    };
}

/* -------------------------- Shared primitives ------------------------- */

function SectionHeader({ icon, title, subtitle, pct, badge, onEdit, actions }) {
    return (
        <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5">{icon}</div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-semibold text-slate-900">{title}</h3>
                    {badge && (
                        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide border ${badge.tone}`}>
                            {badge.icon} {badge.label}
                        </span>
                    )}
                    <span className="ml-1 text-[11px] text-slate-500 font-mono" title="Completion %">{pct}%</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
            </div>
            <div className="flex items-center gap-2">
                {actions}
                {onEdit && (
                    <Button variant="outline" size="sm" onClick={onEdit} className="gap-1.5" data-testid={`${title.toLowerCase().replace(/\s+/g, "-")}-edit-btn`}>
                        <Pencil size={12} weight="bold" /> Edit
                    </Button>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, multiline, className = "" }) {
    return (
        <div className={className}>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
            <div className={`text-sm mt-0.5 ${value ? "text-slate-800" : "text-slate-400"} ${multiline ? "whitespace-pre-wrap leading-snug" : ""}`}>
                {value || "—"}
            </div>
        </div>
    );
}

function BigTextarea({ label, placeholder, value, onChange, rows = 3, testId, hint }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
            <Textarea
                value={value}
                onChange={e => onChange(e.target.value)}
                rows={rows}
                placeholder={placeholder}
                className="mt-1"
                data-testid={testId}
            />
            {hint && <p className="text-[11px] text-slate-400 mt-1 leading-snug">{hint}</p>}
        </div>
    );
}

function SmallList({ label, placeholder, value, onChange, testId }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
            <Textarea
                value={value}
                onChange={e => onChange(e.target.value)}
                rows={3}
                placeholder={placeholder}
                className="mt-1 text-sm"
                data-testid={testId}
            />
            <div className="text-[10px] text-slate-400 mt-0.5">One per line.</div>
        </div>
    );
}

function toList(text, sep = "\n") {
    if (!text || !text.trim()) return null;
    return text.split(sep).map(s => s.trim()).filter(Boolean);
}
